import moment from "moment";
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { toast } from "react-hot-toast";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination/Pagination";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import styles from "./styles.module.scss";
import ChargebackChildRow from "./ChargebackChildRow";
import useChargebackStore from "../../../../stores/chargeback";
import {
  getChargebackPageData,
  getChargebackPageDataExport,
  getChargebackPageDetails,
} from "../../../../../api";
import Loader from "../../../../../components/loader/loader";
import CaretRight from "../../../../assets/svg/icons/common/CaretRight";
import MerchantNameLink from "../../../../components/MerchantNameLink";

// Helper functions
const formatCardNumber = (number) => {
  if (!number) return "";
  // Get last 4 digits, pad with spaces if less than 4
  const lastFour = number.slice(-4).padStart(4, " ");
  return `***${lastFour}`;
};

const formatCurrency = (amount) => {
  if (!amount) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Math.abs(parseFloat(amount)));
};

const formatDate = (date) => {
  if (!date) return "";
  return moment(date).format("MM/DD/YYYY");
};

const cards_mapping = [
  { id: "VD", name: "Visa Debit", icon: "/images/visa24x24.svg" },
  { id: "MD", name: "MasterCard Debit", icon: "/images/master24x24.svg" },
  { id: "AM", name: "American Express", icon: "/images/amex24x24.svg" },
  { id: "VS", name: "Visa Credit", icon: "/images/visa24x24.svg" },
  { id: "MC", name: "MasterCard Credit", icon: "/images/master24x24.svg" },
  { id: "DS", name: "Discover", icon: "/images/discover24x24.svg" },
  { id: "MB", name: "Maestro", icon: "/images/debit24x24.svg" },
  { id: "VB", name: "Visa Business", icon: "/images/visa24x24.svg" },
];

const ChargebackList = forwardRef(({ processor }, ref) => {
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortBy, setSortBy] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [downloadingRowId, setDownloadingRowId] = useState(null);
  const [data, setData] = useState([]);
  // Details are loaded lazily from the server when a row is first expanded —
  // keeps the list endpoint off the remote replica's Chargeback_Details table.
  const [detailsByRowId, setDetailsByRowId] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(new Set());
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });

  // Get filters from the store
  const { filters } = useChargebackStore();

  // Monotonic id for the in-flight list request — see fetchData.
  const requestSeqRef = useRef(0);
  // Last filters object the fetch effect saw, so it can tell a filter change
  // (reset to page 1) from a page change (fetch as-is). Declared after the
  // store read: it seeds from `filters`.
  const filtersRef = useRef(filters);

  // Initialize with default date range (current month)
  const defaultDateRange = {
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
  };

  const getChargebackPageDataParams = () => {
    // Use the dates directly from filters or default
    const startDate =
      filters.dateRange?.startDate || defaultDateRange.startDate;
    const endDate = filters.dateRange?.endDate || defaultDateRange.endDate;

    // Format amount and days to rebut ranges
    const amountRange = filters.amount
      ? {
          amount_from: filters.amount.from,
          amount_to: filters.amount.to,
        }
      : null;

    const daysRange = filters.daysToRebut
      ? {
          days_from: filters.daysToRebut.from,
          days_to: filters.daysToRebut.to,
        }
      : null;

    const params = {
      start_date: startDate,
      end_date: endDate,
      page,
      search: filters.searchTerm,
      cards: filters.cards,
      ...amountRange,
      ...daysRange,
      ...(filters.isDaysToRebutNull ? { is_days_to_rebut_null: true } : {}),
      reasons: filters.reasons?.map((r) => r.value),
      status: filters.status,
      org_filter: filters.orgFilter || "",
    };

    return params;
  };

  const fetchData = async () => {
    // Responses can land out of order. A search drops the date filter, so a
    // broad term scans the whole table while a longer one is answered from an
    // index — an earlier request can resolve after a later one and paint the
    // list with results for a term the user has already replaced. Each call
    // takes a sequence number and only the newest one is allowed to write
    // state; stale responses are dropped, including their loader reset.
    const seq = ++requestSeqRef.current;
    const isCurrentRequest = () => seq === requestSeqRef.current;

    try {
      setLoading(true);

      const params = getChargebackPageDataParams();

      const response = await getChargebackPageData(params);

      if (!isCurrentRequest()) return;

      if (response?.status === true && response?.data) {
        const paginatedData = response.data;

        if (!paginatedData?.data || !Array.isArray(paginatedData.data)) {
          setData([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: 10,
          });
          return;
        }

        // Transform API data to match our table structure
        const transformedData = paginatedData.data.map((item) => ({
          id: item.id,
          dba: item.dba || "",
          powerapp_id: item.powerapp_id ?? null,
          card_type: item?.card_type || "",
          card: formatCardNumber(item.card_number),
          auth_number: item.authorization_number || "",
          cb_amount: formatCurrency(item.chargeback_amount) || "$0.00",
          cb_date: formatDate(item.chargeback_date) || "",
          last_action_date: item.last_act_date
            ? formatDate(item.last_act_date)
            : "",
          age: item.days_old?.toString() || "0",
          to_rebut: item.days_to_rebut?.toString() || "0",
          reason: item.chargeback_description || "",
          represented: item.represent_date
            ? formatDate(item.represent_date)
            : "",
          has_details: !!item.has_details,
        }));

        // Drop cached details from the previous page — dash_ids may collide.
        setExpandedRows(new Set());
        setDetailsByRowId({});
        setDetailsLoading(new Set());
        setData(transformedData);
        setPagination({
          currentPage: paginatedData.current_page || 1,
          totalPages: paginatedData.last_page || 1,
          totalItems: paginatedData.total || 0,
          perPage: paginatedData.per_page || 10,
        });
      } else {
        setData([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          perPage: 10,
        });
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      toast.error("Failed to fetch chargeback data");
      setData([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 10,
      });
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  // A filter change has to return the list to page 1. The result set changes
  // size underneath the paginator, so holding page 3 while a search narrows the
  // matches to a single page asks the server for a page that does not exist and
  // renders a search that did match as "no results".
  //
  // When the filters change off page 1 this only resets the page and skips the
  // fetch: setPage re-runs the effect with page 1, so the request fires once
  // with the right page instead of once per state change.
  useEffect(() => {
    const filtersChanged = filtersRef.current !== filters;
    filtersRef.current = filters;

    if (filtersChanged && page !== 1) {
      setPage(1);
      return;
    }

    fetchData();
  }, [filters, page]);

  const toggleRow = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
      setExpandedRows(newExpandedRows);
      return;
    }
    newExpandedRows.add(id);
    setExpandedRows(newExpandedRows);

    // Lazy-load details on first expand; cache thereafter for the lifetime of
    // the current page of results.
    if (detailsByRowId[id] !== undefined || detailsLoading.has(id)) return;

    setDetailsLoading((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    getChargebackPageDetails(id)
      .then((response) => {
        const details = response?.status ? response.data : [];
        setDetailsByRowId((prev) => ({ ...prev, [id]: details || [] }));
      })
      .catch(() => {
        toast.error("Failed to load chargeback details");
        // Don't cache on error — leave the key unset so a collapse + reopen
        // retries the fetch instead of showing an empty list forever.
      })
      .finally(() => {
        setDetailsLoading((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortColumn(sortColumn === "ASC" ? "DESC" : "ASC");
    } else {
      // New field, default to ASC
      setSortBy(field);
      setSortColumn("ASC");
    }
  };

  // Sort the data based on current sort settings
  const sortedData = React.useMemo(() => {
    if (!sortBy || !sortColumn) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (sortColumn === "ASC") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [sortBy, sortColumn, data]);

  const renderExpandedContent = (row) => {
    if (detailsLoading.has(row.id)) {
      return (
        <div style={{ padding: "12px 16px" }}>
          <Loader />
        </div>
      );
    }
    return <ChargebackChildRow details={detailsByRowId[row.id] || []} />;
  };

  const columns = [
    {
      field: "expand",
      width: "40px",
      headerName: "",
      renderCell: (_, row) => {
        if (!row.has_details) return null;
        return (
          <button
            className={`${styles.expandButton} ${expandedRows.has(row.id) ? styles.rotated : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(row.id);
            }}
          >
            <CaretRight />
          </button>
        );
      },
    },
    {
      field: "dba",
      headerName: "DBA",
      width: "150px",
      allowSort: true,
      renderCell: (value, row) => (
        <div className={styles.dbaCell}>
          <MerchantNameLink powerappId={row.powerapp_id}>
            {value}
          </MerchantNameLink>
        </div>
      ),
    },
    {
      field: "card",
      headerName: "CARD",
      width: "150px",
      allowSort: true,
      renderCell: (value) => <div className={styles.normalCell}>{value}</div>,
    },
    {
      field: "card_type",
      headerName: "CARD TYPE",
      width: "150px",
      allowSort: true,
      renderCell: (value, row) => {
        const cardMapping = getCardMapping(value);
        return (
          <div className={`${styles.normalCell} ${styles.card_type}`}>
            {value}
            <span>
              <img src={cardMapping.icon} alt={cardMapping.name} />
            </span>
          </div>
        );
      },
    },
    {
      field: "auth_number",
      headerName: "AUTH. NUMBER",
      width: "120px",
      allowSort: true,
      renderCell: (value) => <div className={styles.normalCell}>{value}</div>,
    },
    {
      field: "cb_amount",
      headerName: "CB AMOUNT",
      width: "120px",
      allowSort: true,
      renderCell: (value) => <div className={styles.amountCell}>{value}</div>,
    },
    {
      field: "cb_date",
      headerName: "CB DATE",
      width: "120px",
      allowSort: true,
      renderCell: (value) => <div className={styles.normalCell}>{value}</div>,
    },
    {
      field: "last_action_date",
      headerName: "LAST ACTION",
      width: "120px",
      allowSort: true,
      renderCell: (value) => (
        <div className={styles.normalCell}>{value || "-"}</div>
      ),
    },
    {
      field: "age",
      headerName: "AGE",
      width: "80px",
      allowSort: true,
      renderCell: (value) => {
        const days = parseInt(value);
        return (
          <div className={styles.normalCell}>
            {`${days} ${days === 1 ? "day" : "days"}`}
          </div>
        );
      },
    },
    {
      field: "to_rebut",
      headerName: "TO REBUT",
      width: "100px",
      allowSort: true,
      renderCell: (value) => {
        const days = parseInt(value);
        const text = `${days} ${days === 1 ? "day" : "days"}`;

        if (days < 3) {
          return (
            <div className={`${styles.badge} ${styles.redBadge}`}>{text}</div>
          );
        } else if (days >= 3 && days <= 7) {
          return (
            <div className={`${styles.badge} ${styles.yellowBadge}`}>
              {text}
            </div>
          );
        } else {
          return <div className={styles.normalCell}>{text}</div>;
        }
      },
    },
    {
      field: "reason",
      headerName: "REASON",
      width: "200px",
      allowSort: true,
      renderCell: (value) => (
        <div title={value} className={styles.normalCell}>
          {value}
        </div>
      ),
    },
    {
      field: "represented",
      headerName: "REPRESENTED",
      width: "120px",
      allowSort: true,
      renderCell: (value) =>
        !value ? (
          ""
        ) : (
          // <button
          //   onClick={() => {}}
          //   className={styles.submitButton}
          // >
          //   Submit
          // </button>
          <div className={styles.representedCell}>{value}</div>
        ),
    },
    {
      field: "download",
      width: "128px",
      headerName: "",
      renderCell: (_, row) => {
        const busy = downloadingRowId === row.id;
        return (
          <ButtonIcon
            bgColor="#EDEDF4"
            className={styles.rowDownloadBtn}
            title="Download this chargeback and line items (CSV)"
            disabled={busy}
            onClick={(e) => handleSingleRowDownload(row, e)}
          >
            Download
          </ButtonIcon>
        );
      },
    },
  ];

  const getCardMapping = (card_type) => {
    return (
      cards_mapping.find((card) => card.id === card_type) || {
        id: card_type,
        name: "Unknown Card",
        icon: "/images/unknown24x24.svg",
      }
    );
  };

  // The export endpoint answers with JSON instead of a file when it refuses —
  // any export over the server's row limit, or a single row that falls outside
  // the current filters. The message names the row count and how to get under
  // the limit, so it has to reach the user. The request uses responseType
  // "blob", so that message arrives as a Blob and must be read back out.
  const readErrorMessage = async (error) => {
    const data = error?.response?.data;
    try {
      const payload =
        data instanceof Blob ? JSON.parse(await data.text()) : data;
      return payload?.message || null;
    } catch {
      return null;
    }
  };

  const handleSingleRowDownload = async (row, e) => {
    e.stopPropagation();
    setDownloadingRowId(row.id);
    try {
      const params = {
        ...getChargebackPageDataParams(),
        export: true,
        dash_id: row.id,
      };
      const response = await getChargebackPageDataExport(params);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `chargeback-${row.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (error) {
      console.log({ error });
      toast.error(
        (await readErrorMessage(error)) || "Failed to download this chargeback",
      );
    }
    setDownloadingRowId(null);
  };

  const onExportClickHandler = async () => {
    setIsExporting(true);
    try {
      const params = getChargebackPageDataParams();
      params.export = true;
      const response = await getChargebackPageDataExport(params);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const date = new Date(filters.dateRange.startDate);
      const monthNames = [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sept",
        "oct",
        "nov",
        "dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const fileName = `chargeback-${month}-${year}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("CSV downloaded successfully");
    } catch (error) {
      console.log({ error });
      toast.error((await readErrorMessage(error)) || "Failed to download CSV");
    }
    setIsExporting(false);
  };

  useImperativeHandle(ref, () => ({
    triggerExport: onExportClickHandler,
  }));

  return (
    <div className={styles.tableContainer}>
      {isExporting && <Loader />}
      {!!filters.searchTerm && (
        <div className={styles.search_scope_note}>
          Showing matches from all dates — the selected period does not limit
          search results.
        </div>
      )}
      <Table
        columns={columns}
        data={sortedData.map((row) => ({
          ...row,
          expanded: expandedRows.has(row.id),
        }))}
        renderExpandedContent={renderExpandedContent}
        loading={loading}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortBy={sortBy}
        className={styles.chargebackTable}
        disablePaddingOnExpandedRow={true}
      />
      {sortedData.length > 0 && (
        <div className={styles.pagination_container}>
          <Pagination
            pageCount={pagination.totalPages}
            currentPage={page - 1}
            onPageChange={({ selected }) => {
              console.log("selected", selected);
              setPage(selected + 1);
            }}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.perPage}
          />
        </div>
      )}
    </div>
  );
});

ChargebackList.displayName = "ChargebackList";

export default ChargebackList;
